import * as Ably from 'ably';
import {useRef, useState} from 'react';
import {fetchAblyWebSocketAuthToken} from 'store/services/webSocketServices';
import {CompositionChannelMessage} from 'utils/types/newReviewed';

const MESSAGE_TIME_LIMIT = 15 * 1000; // 30 seconds

interface UseAblyWebsocketParams {
  onError(): void;
}

export function useAblyWebsocket({onError}: UseAblyWebsocketParams) {
  const [channelRef, setChannelRef] = useState<string>('');
  const [messages, setMessages] = useState<CompositionChannelMessage[]>([]);

  const ablyRef = useRef<Ably.Realtime>();
  const timer = useRef<NodeJS.Timeout>();

  const messageTimer = {
    refresh: () => timer.current?.refresh(),
    clear: () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = undefined;
      }
    },
    start: () => {
      if (timer.current) return;
      timer.current = setInterval(handleError, MESSAGE_TIME_LIMIT);
    },
  };

  function getAuthToken(id?: string) {
    return async (_, callback) => {
      try {
        const {status, data} = await fetchAblyWebSocketAuthToken(id);
        const isSuccessful = status >= 200 && status < 300;

        if (isSuccessful) {
          callback(null, data);
        } else {
          callback(`Token request failed with status ${status}`, null);
        }
      } catch (error: any) {
        callback(error?.message ?? 'Token request failed', null);
      }
    };
  }

  function initialize(composition_id?: string) {
    const channelName = `composition-${composition_id}`;
    setChannelRef(channelName);

    // do not open a new connection if there is already one in progress or connected
    const state = ablyRef.current?.connection.state;
    if (
      state === 'connected' ||
      state === 'connecting' ||
      state === 'initialized'
    )
      return;

    // close any previous failed/suspended instance before reinitializing
    if (ablyRef.current) {
      ablyRef.current.close();
      ablyRef.current = undefined;
    }

    messageTimer.start();

    const ably = new Ably.Realtime({
      logLevel: 1, // errors trigger the log handler
      closeOnUnload: true,
      logHandler: () => handleError(),
      authCallback: getAuthToken(composition_id),
    });

    ablyRef.current = ably;

    ablyRef.current.connection.on('connected', () => {
      subscribe(getChannel(channelName, {rewind: '6'}));
    });

    ablyRef.current.connection.on(['failed', 'suspended'], () => {
      if (!timer.current) return;
      handleError();
    });
  }

  function getChannel(name: string, params: Record<string, string> = {}) {
    if (!ablyRef.current) return null;
    return ablyRef.current.channels.get(name, {params});
  }

  function subscribe(channel: Ably.RealtimeChannel | null) {
    channel?.subscribe(({name, data}) => {
      setMessages((prev) => [...prev, {name: name!, data}]);

      if (name === 'finish') unsubscribe();

      messageTimer.refresh();
    });
  }

  function unsubscribe() {
    const channel = getChannel(channelRef);

    channel?.unsubscribe();
    ablyRef.current?.close();
    ablyRef.current = undefined;
    setChannelRef('');
    messageTimer.clear();
  }

  function handleError() {
    onError();
    unsubscribe();
  }

  return {
    initialize,
    unsubscribe,
    messages,
  };
}
